import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Organisation Settings Service
 * Manager-only (role 2 / 4) read + update of the logged-in user's organisation.
 * Routes dynamically based on role_id (via getManagerRoutePath).
 */

export const getOrganizationSettings = async () => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/organization`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error fetching organisation settings' };
  }
};

export const updateOrganizationSettings = async (payload) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.put(`${API_BASE_URL}/${routePath}/organization`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error updating organisation settings' };
  }
};

/**
 * The organisation's logo. Its own endpoint rather than part of the settings
 * PUT, because it is a file rather than a text field — and because it changes
 * on its own, without saving the whole form.
 */
export const uploadOrganizationLogo = async (file) => {
  try {
    const routePath = getManagerRoutePath();
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(
      `${API_BASE_URL}/${routePath}/organization/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error uploading the logo' };
  }
};

export const removeOrganizationLogo = async () => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(`${API_BASE_URL}/${routePath}/organization/logo`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error removing the logo' };
  }
};

/**
 * The site's shift check-in QR code (care homes only) — carers scan it in the
 * app to start a shift. The first GET creates it; regenerating replaces it, so
 * every printout of the old one stops working.
 */
export const getShiftQrCode = async () => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/organization/shift-qr`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error loading the shift QR code' };
  }
};

export const regenerateShiftQrCode = async () => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/organization/shift-qr/regenerate`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error making a new QR code' };
  }
};

const organizationSettingsService = {
  getOrganizationSettings,
  updateOrganizationSettings,
  uploadOrganizationLogo,
  removeOrganizationLogo,
  getShiftQrCode,
  regenerateShiftQrCode,
};

export default organizationSettingsService;
